import type { InviteCampaign } from '@peles/shared';
import { useSubEvents } from '../../../hooks/useInvites';
import { MessageComposer } from '../../messaging/MessageComposer';
import { StepFooter } from './StepFooter';
import { fromDateInputValue, toDateInputValue, useStepDraft, withSettings } from './useStepDraft';

export interface CampaignStepProps {
  campaign: InviteCampaign;
  eventId: string;
  clientMode: boolean;
  onDirtyChange?: (dirty: boolean) => void;
  /** Move on to the next step. */
  onNext?: () => void;
}

interface DetailsDraft {
  name: string;
  subject: string;
  message: string;
  deadline: string;
  rsvpEventIds: string[];
  rsvpEnabled: boolean;
  allowMaybe: boolean;
  allowPartyEdit: boolean;
  showHebrewDate: boolean;
  showSeatFinder: boolean;
}

function detailsOf(campaign: InviteCampaign): DetailsDraft {
  const settings = campaign.settings ?? {};
  return {
    name: campaign.name,
    subject: campaign.subject ?? '',
    message: campaign.message ?? '',
    deadline: toDateInputValue(campaign.rsvpDeadline),
    rsvpEventIds: campaign.rsvpEventIds ?? [],
    rsvpEnabled: settings.rsvpEnabled !== false,
    allowMaybe: settings.allowMaybe !== false,
    allowPartyEdit: settings.allowPartyEdit !== false,
    showHebrewDate: settings.showHebrewDate === true,
    showSeatFinder: settings.showSeatFinder === true,
  };
}

const inputClass =
  'mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

/** Step 1: what the invitation says, when answers are due, and what can be answered. */
export function CampaignDetailsStep({
  campaign,
  eventId,
  clientMode,
  onDirtyChange,
  onNext,
}: CampaignStepProps) {
  const { data: subEvents = [] } = useSubEvents(eventId);
  const { draft, set, dirty, save, saving } = useStepDraft(
    campaign,
    detailsOf,
    (d, c) => ({
      name: d.name.trim() || c.name,
      subject: d.subject.trim() || null,
      message: d.message || null,
      rsvpDeadline: fromDateInputValue(d.deadline),
      rsvpEventIds: d.rsvpEventIds,
      settings: withSettings(c, {
        rsvpEnabled: d.rsvpEnabled,
        allowMaybe: d.allowMaybe,
        allowPartyEdit: d.allowPartyEdit,
        showHebrewDate: d.showHebrewDate,
        showSeatFinder: d.showSeatFinder,
      }),
    }),
    onDirtyChange,
  );

  const toggle = (key: keyof DetailsDraft, label: string, hint?: string, disabled?: boolean) => (
    <label className="flex items-start gap-2 text-sm text-gray-700">
      <input
        type="checkbox"
        className="mt-0.5"
        checked={draft[key] as boolean}
        disabled={disabled}
        onChange={(e) => set(key, e.target.checked as never)}
      />
      <span>
        {label}
        {hint && <span className="block text-xs text-gray-500">{hint}</span>}
      </span>
    </label>
  );

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="campaign-name" className="block text-sm font-medium text-gray-700">
            Campaign name
          </label>
          <input
            id="campaign-name"
            value={draft.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="Main invitation"
            className={inputClass}
          />
          <p className="mt-1 text-xs text-gray-500">For you — guests never see it.</p>
        </div>
        <div>
          <label htmlFor="campaign-deadline" className="block text-sm font-medium text-gray-700">
            Answer by
          </label>
          <input
            id="campaign-deadline"
            type="date"
            value={draft.deadline}
            onChange={(e) => set('deadline', e.target.value)}
            className={inputClass}
          />
        </div>
      </div>

      <div>
        <label htmlFor="campaign-subject" className="block text-sm font-medium text-gray-700">
          Email subject
        </label>
        <input
          id="campaign-subject"
          value={draft.subject}
          onChange={(e) => set('subject', e.target.value)}
          placeholder="You're invited!"
          className={inputClass}
        />
      </div>

      <MessageComposer
        id="campaign-message"
        label="Message"
        value={draft.message}
        onChange={(value) => set('message', value)}
        rows={4}
        maxLength={10000}
        placeholder="We would be honored to have you join us…"
        record={{ entityType: 'Event', entityId: eventId }}
        assistant={!clientMode}
        savedReplies={!clientMode}
        subject={draft.subject}
        onSubjectChange={(subject) => set('subject', subject)}
      />

      <fieldset className="space-y-2">
        <legend className="text-sm font-medium text-gray-700">Answers</legend>
        {toggle(
          'rsvpEnabled',
          'Collect RSVPs',
          draft.rsvpEnabled
            ? undefined
            : 'Information only — guests see the invitation and the event details, with no form.',
        )}
        {toggle('allowMaybe', 'Offer "Maybe"', undefined, !draft.rsvpEnabled)}
        {toggle(
          'allowPartyEdit',
          'Guests may say who of their party is coming',
          undefined,
          !draft.rsvpEnabled,
        )}
        {toggle('showHebrewDate', 'Show the Hebrew date')}
        {toggle('showSeatFinder', 'Let guests find their seat from their invitation')}
      </fieldset>

      {subEvents.length > 0 && (
        <fieldset>
          <legend className="text-sm font-medium text-gray-700">Also ask about</legend>
          <div className="mt-1 flex flex-wrap gap-2">
            {subEvents.map((subEvent) => {
              const on = draft.rsvpEventIds.includes(subEvent.id);
              return (
                <button
                  key={subEvent.id}
                  type="button"
                  aria-pressed={on}
                  onClick={() =>
                    set(
                      'rsvpEventIds',
                      on
                        ? draft.rsvpEventIds.filter((id) => id !== subEvent.id)
                        : [...draft.rsvpEventIds, subEvent.id],
                    )
                  }
                  className={`rounded-full border px-3 py-1.5 text-sm ${
                    on
                      ? 'border-primary-600 bg-primary-50 text-primary-800'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {subEvent.name}
                </button>
              );
            })}
          </div>
        </fieldset>
      )}

      <StepFooter
        dirty={dirty}
        saving={saving}
        onSave={() => void save()}
        onNext={
          onNext &&
          (async () => {
            if (dirty && !(await save())) return;
            onNext();
          })
        }
        nextLabel="Save & continue"
      />
    </div>
  );
}
